import { type FC, useMemo } from "react";

import { type IMarket } from "~/types/event";
import { formatDate } from "~/utils/formatDate";

import * as Styled from "./MarketCountDown.styles";
import { useMarketCountDown } from "./useMarketCountDown";

interface IMarketCountDownProps {
  market: IMarket;
  resolved?: boolean;
  hideLabel?: boolean;
}

export const MarketCountDown: FC<IMarketCountDownProps> = ({
  market,
  resolved,
  hideLabel = false,
}) => {
  const { dd, hh, mm, ss, isRed, isMoreThan30Days } = useMarketCountDown(
    market.endDate
  );
  const ddText = useMemo(() => (dd ? dd + "d:" : ""), [dd]);
  const ssText = useMemo(() => (dd ? "" : `:${ss}s`), [dd, ss]);
  const isHide = useMemo(
    () => !dd && hh === "00" && mm === "00" && ss == "00",
    [dd, hh, mm, ss]
  );

  const resolvedAtDate = useMemo(
    () =>
      market.closedTime
        ? formatDate(market.closedTime || "")
        : formatDate(market.endDate || ""),
    [market.closedTime, market.endDate]
  );

  const label = useMemo(() => (hideLabel ? "" : "Time left:"), [hideLabel]);

  if (resolved) {
    return (
      <Styled.Root $red={isRed}>
        <Styled.Text $red={isRed} variant="body1" sx={{ fontWeight: 600 }}>
          Closed: {resolvedAtDate}
        </Styled.Text>
      </Styled.Root>
    );
  }

  if (isHide || isMoreThan30Days) {
    return null;
  }

  return (
    <Styled.Root $red={isRed}>
      <Styled.Text $red={isRed} variant="body1" sx={{ fontWeight: 600 }}>
        {label} {ddText}
        {hh}h:{mm}m{ssText}
      </Styled.Text>
    </Styled.Root>
  );
};
